/**
 * THE editor context behavior table — one declarative row per context, no
 * context is "the default" and none is "the exception". Editors consume named
 * CAPABILITIES (behavior.autoFocus, behavior.controlledOpen, …), never test
 * "which context am I" inline. Root-cause lesson (CEO, 2026-06-12): the
 * original editors inferred "editing" from being mounted; a widget never
 * infers its state from its lifecycle — state arrives explicitly. A future
 * context ('inline-row', 'bulk-edit', …) is a NEW ROW here, not a new
 * scattering of conditionals.
 *
 * Enforced by the form-context registry contract test (closed-on-mount,
 * commit-closes, no focus-steal, quiet chrome).
 */

export type EditorContext = 'cell' | 'filter' | 'form'

export interface EditorBehavior {
  /** Grab focus on mount. True where mount = the user just started editing. */
  autoFocus: boolean
  /** Popover editors: closed trigger on mount, open on click, commit closes.
   *  False = uncontrolled defaultOpen (the mount IS the open). */
  controlledOpen: boolean
  /** Popover dismissal exits the edit (onCancel) — true where the editor's
   *  lifetime IS the edit session and dismissing means "stop editing". */
  dismissCancels: boolean
  /** Escape is consumed LOCALLY (stopPropagation): cancelling the draft must
   *  not also close the surface hosting the editor (record stage, peek rail,
   *  table focus). False where an outer surface owns Escape — a form field
   *  lets it bubble so the modal's topmost-Escape contract fires. */
  escapeIsLocal: boolean
  /** SEN-197: a wrapping editor (long text) may EXPAND past its host — a box
   *  pinned to the cell's top-left at its width, growing down over the rows.
   *  True where the host is a fixed-size table cell whose layout must not
   *  reflow; false where the editor lives in a layout that sizes it (a filter
   *  popover, a form field). */
  expands: boolean
  /** Control chrome. Always-on ring = "this cell is editing now"; quiet =
   *  plain border, ring only on focus (a persistent field is not an edit
   *  session — an always-on ring reads as a wall of boxes / fake required
   *  markers; required is the muted asterisk). */
  chrome: string
}

const RING = 'ring-2 ring-ring'
const QUIET = 'border border-border focus:ring-2 focus:ring-ring focus-within:ring-2 focus-within:ring-ring'

const BEHAVIOR: Record<EditorContext, EditorBehavior> = {
  /** Mounted only while editing one cell; unmounts on commit/cancel. */
  cell:   { autoFocus: true,  controlledOpen: false, dismissCancels: true,  escapeIsLocal: true,  expands: true,  chrome: RING },
  /** Mounted while a filter value is being set; commits live per change. */
  filter: { autoFocus: true,  controlledOpen: false, dismissCancels: true,  escapeIsLocal: true,  expands: false, chrome: RING },
  /** Mounted persistently as one field among many; the FORM owns focus. */
  form:   { autoFocus: false, controlledOpen: true,  dismissCancels: false, escapeIsLocal: false, expands: false, chrome: QUIET },
}

export function editorBehavior(context: EditorContext | undefined): EditorBehavior {
  return BEHAVIOR[context ?? 'cell']
}

export function controlChrome(context: EditorContext | undefined): string {
  return editorBehavior(context).chrome
}
